import { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Portal } from "../Portals";
import useFocusTrap from "../../hooks/useFocusTrap";
import AIInterviewStatusCell from "../aiInterview/AIInterviewStatusCell";
import AIInterviewDetailsModal from "../aiInterview/AIInterviewDetailsModal";
import { aiSummaryOf } from "../../utils/aiInterviewApi";
import useAISessions from "../../hooks/useAISessions";
import { fetchInterviewFeedback } from "../../utils/interviewApi";
import { AIModeCaption, FeedbackSummaryCell, InterviewTime, PanelistNames, StatusHighlight } from "../interviews/InterviewCells";
import InterviewFeedbackModal from "../interviews/InterviewFeedbackModal";

// InterviewTime reads the raw API field names
const timeFieldsOf = (interview) => ({
  start_utc: interview.startTime || interview.startLocal,
  end_utc: interview.endTime || interview.endLocal,
  timezone: interview.timezone,
});

const isPendingStatus = (status) => (status || "").startsWith("pending");

export default function InterviewListModal({ isOpen, onClose, interviews, jobs = [], interviewCount, candidate }) {
  const theme = useTheme();
  const [aiDetailsInterview, setAiDetailsInterview] = useState(null);
  const [feedbackInterview, setFeedbackInterview] = useState(null);
  // interview id → server feedback summary (null when none or it failed to load)
  const [feedbackSummaries, setFeedbackSummaries] = useState({});
  const modalRef = useFocusTrap(isOpen && !aiDetailsInterview && !feedbackInterview, onClose);
  // This candidate's AI sessions (mode, round, warnings); live ones refresh every 15 s
  const candidateId = (interviews || []).find((interview) => interview.candidateId)?.candidateId || candidate?.candidate_id || "";
  const { sessions: aiSessions, update: updateAISession } = useAISessions({
    autoLoad: isOpen && Boolean(candidateId),
    filters: { candidate_id: candidateId },
    interviews: isOpen
      ? (interviews || []).map((interview) => ({ id: interview.id, startTime: interview.startTime, source: interview }))
      : [],
    paused: Boolean(aiDetailsInterview),
  });

  const loadFeedbackSummary = (interviewId) =>
    fetchInterviewFeedback(interviewId)
      .then(({ summary }) => setFeedbackSummaries((current) => ({ ...current, [interviewId]: summary })))
      .catch((err) => {
        console.error("Error fetching interview feedback:", err);
        setFeedbackSummaries((current) => ({ ...current, [interviewId]: null }));
      });

  const feedbackIds = (interviews || [])
    .filter((interview) => interview.id && !isPendingStatus(interview.backendStatus))
    .map((interview) => interview.id)
    .join(",");

  useEffect(() => {
    if (!isOpen) return;
    feedbackIds.split(",").filter(Boolean).forEach(loadFeedbackSummary);
    // loadFeedbackSummary only sets state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, feedbackIds]);

  if (!isOpen) return null;

  const count = interviewCount ?? interviews.length;
  const headerCellSx = {
    backgroundColor: theme.palette.secondary.v5,
    color: theme.palette.text.main,
    zIndex: 10,
  };
  // Same cell spacing as the Interviews page table
  const tableSx = {
    backgroundColor: theme.palette.primary.main,
    "& .MuiTableCell-root": { paddingLeft: "15px", paddingRight: 0 },
    "& .MuiTableCell-root:first-of-type": { paddingLeft: 3 },
    "& .MuiTableCell-root:last-of-type": { paddingRight: 3 },
  };

  return (
    <>
    <Portal>
      <div
        className="z-50 bg-black bg-opacity-70"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 16px",
        }}
        onClick={onClose}
      >
        <div
          ref={modalRef}
          className="bg-white rounded-lg shadow-xl flex flex-col overflow-hidden"
          style={{ width: "min(1120px, calc(100vw - 32px))", maxHeight: "calc(100dvh - 80px)" }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="interview-list-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="flex shrink-0 justify-between items-center py-4 px-5 border-b"
            style={{ borderColor: theme.palette.secondary.v4 }}
          >
            <h2 id="interview-list-title" className="font-semibold text-lg">
              Interviews
              <span className="font-normal" style={{ color: theme.palette.text.secondary }}>
                {" "}({count})
              </span>
            </h2>
            <button
              onClick={onClose}
              aria-label="Close interview list"
              className="inline-flex items-center justify-center rounded-full bg-black text-white h-6 w-6 hover:bg-gray-800 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <div
            className="overflow-auto border rounded-md m-6 max-md:mx-4"
            style={{ color: theme.palette.text.main, borderColor: theme.palette.secondary.v4, maxHeight: "min(420px, calc(100dvh - 220px))" }}
          >
            {interviews.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center h-40 text-accent-gray"
                style={{ background: theme.palette.primary.main }}
              >
                <p className="text-lg font-medium mb-1">No interviews scheduled</p>
                <p className="text-sm max-w-md text-center px-6">
                  Scheduled interviews will appear here.
                </p>
              </div>
            ) : (
              <Table stickyHeader className="w-full min-w-max" sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Candidate</TableCell>
                    <TableCell sx={headerCellSx}>Panelists</TableCell>
                    <TableCell sx={headerCellSx}>Job</TableCell>
                    <TableCell sx={headerCellSx}>Round</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                    <TableCell sx={headerCellSx}>Feedback</TableCell>
                    <TableCell sx={headerCellSx}>Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {interviews.map((interview, index) => {
                    const jobTitle =
                      interview.jdTitle ||
                      jobs.find((job) => job.id === interview.jdId)?.title ||
                      "—";
                    const panelistNames = interview.panelistNames?.length
                      ? interview.panelistNames
                      : (interview.interviewerName || "").split(",").map((name) => name.trim()).filter(Boolean);
                    const aiSummary = aiSummaryOf(interview, aiSessions[interview.id]);
                    return (
                      <TableRow key={interview.id || `${interview.jdId}-${index}`}>
                        <TableCell>
                          <Typography sx={{ fontSize: "0.875rem" }}>{interview.candidateName || candidate?.name || "—"}</Typography>
                          <Typography sx={{ fontSize: "0.75rem", wordBreak: "break-all" }}>{interview.candidateEmail || candidate?.email || ""}</Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: "200px" }}>
                          <PanelistNames names={panelistNames} aiMode={interview.aiMode} />
                        </TableCell>
                        <TableCell sx={{ maxWidth: "260px" }}>{jobTitle}</TableCell>
                        <TableCell sx={{ maxWidth: "200px" }}>{interview.roundName || "—"}</TableCell>
                        <TableCell>
                          {/* The AI interviewer's status when AI is on, otherwise the interview status */}
                          {aiSummary?.enabled ? (
                            <AIInterviewStatusCell
                              interviewId={interview.id}
                              summary={aiSummary}
                              onOpen={() => setAiDetailsInterview(interview)}
                            />
                          ) : (
                            <>
                              <StatusHighlight status={interview.backendStatus} />
                              <AIModeCaption aiMode={interview.aiMode} />
                            </>
                          )}
                        </TableCell>
                        <TableCell>
                          <FeedbackSummaryCell
                            summary={feedbackSummaries[interview.id]}
                            loaded={isPendingStatus(interview.backendStatus) || interview.id in feedbackSummaries}
                            // Feedback is added from the Interviews page; here it's view-only until it exists
                            canAdd={false}
                            onOpen={() =>
                              setFeedbackInterview({
                                // InterviewFeedbackModal reads the raw API field names
                                interview_id: interview.id,
                                status: interview.backendStatus,
                                ai_mode: interview.aiMode,
                                jd_title: jobTitle === "—" ? "" : jobTitle,
                                round_name: interview.roundName,
                                candidate: {
                                  name: interview.candidateName || candidate?.name || "",
                                  email: interview.candidateEmail || candidate?.email || "",
                                },
                              })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <InterviewTime interview={timeFieldsOf(interview)} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>
    </Portal>
    <InterviewFeedbackModal
      isOpen={Boolean(feedbackInterview)}
      interview={feedbackInterview}
      onClose={() => setFeedbackInterview(null)}
      onFeedbackChange={loadFeedbackSummary}
      onOpenAIReport={() => {
        setAiDetailsInterview((interviews || []).find((item) => item.id === feedbackInterview?.interview_id) || null);
        setFeedbackInterview(null);
      }}
    />
    <AIInterviewDetailsModal
      isOpen={Boolean(aiDetailsInterview)}
      onClose={() => setAiDetailsInterview(null)}
      interviewId={aiDetailsInterview?.id}
      title={aiDetailsInterview?.candidateName || candidate?.name || ""}
      subtitle={[aiDetailsInterview?.jdTitle, aiDetailsInterview?.roundName].filter(Boolean).join(" · ")}
      startTime={aiDetailsInterview?.startTime}
      onSessionChange={updateAISession}
    />
    </>
  );
}
